import api from './api';

export const getClients = async () => {
  try {
    // En producción o desarrollo sin backend, usa datos mock
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_USE_MOCK === 'true') {
      console.log('Using mock clients data');
      return getMockClients();
    }
    
    const response = await api.get('/clients');
    return response.data;
  } catch (error) {
    console.error('Error en getClients:', error);
    // Si hay un error, devuelve datos mock en lugar de fallar
    return getMockClients();
  }
};

export const getClient = async (id) => {
  try {
    // En producción o desarrollo sin backend, usa datos mock
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_USE_MOCK === 'true') {
      console.log(`Using mock client data for id: ${id}`);
      const clients = getMockClients();
      const client = clients.find(c => c.id === parseInt(id));
      if (!client) {
        throw new Error('Cliente no encontrado');
      }
      return client;
    }
    
    const response = await api.get(`/clients/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error en getClient con id ${id}:`, error);
    // Si es un error 404, lanzar el error, de lo contrario intentar con mock
    if (error.response && error.response.status === 404) {
      throw error;
    }
    
    // Intentar con datos mock
    const clients = getMockClients();
    const client = clients.find(c => c.id === parseInt(id));
    if (!client) {
      throw new Error('Cliente no encontrado');
    }
    return client;
  }
};

export const createClient = async (clientData) => {
  try {
    // Si hay archivos nuevos, necesitamos enviar como FormData
    if (clientData.newFiles && clientData.newFiles.length > 0) {
      const formData = new FormData();
      
      // Añadir datos básicos
      formData.append('name', clientData.name);
      formData.append('phone', clientData.phone);
      formData.append('email', clientData.email || '');
      formData.append('address', clientData.address || '');
      formData.append('personalMessage', clientData.personalMessage || '');
      
      // Añadir archivos
      clientData.newFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.post('/clients', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } else {
      // Sin archivos nuevos, envío normal JSON
      const response = await api.post('/clients', clientData);
      return response.data;
    }
  } catch (error) {
    console.error('Error en createClient:', error);
    throw error;
  }
};

export const updateClient = async (id, clientData) => {
  try {
    // Si hay archivos nuevos, necesitamos enviar como FormData
    if (clientData.newFiles && clientData.newFiles.length > 0) {
      const formData = new FormData();
      
      // Añadir datos básicos
      formData.append('name', clientData.name);
      formData.append('phone', clientData.phone);
      formData.append('email', clientData.email || '');
      formData.append('address', clientData.address || '');
      formData.append('personalMessage', clientData.personalMessage || '');
      
      // Añadir IDs de archivos existentes que se mantienen
      if (clientData.files && clientData.files.length > 0) {
        formData.append('existingFiles', JSON.stringify(
          clientData.files.map(file => file.id)
        ));
      }
      
      // Añadir archivos nuevos
      clientData.newFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.put(`/clients/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } else {
      // Sin archivos nuevos, envío normal JSON
      const response = await api.put(`/clients/${id}`, clientData);
      return response.data;
    }
  } catch (error) {
    console.error(`Error en updateClient con id ${id}:`, error);
    throw error;
  }
};

export const deleteClient = async (id) => {
  try {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error en deleteClient con id ${id}:`, error);
    throw error;
  }
};

// Mock para desarrollo/testing
export const getMockClients = () => {
  return [
    {
      id: 1,
      name: 'Miller Roa',
      phone: '1213442424',
      email: 'Miller@mail.com',
      address: 'Rodney 154',
      personalMessage: '¡Vamos Dale Miller! \n¡Venís re bien con la rutina, seguí así! \n No le aflojés a la dieta que es fundamental para ver resultados \n ¡A seguir metiéndole con todo!',
      files: [
        { id: 1, name: 'Cardiología.PDF' },
        { id: 2, name: 'Cardiología.PDF' },
        { id: 3, name: 'Cardiología.PDF' }
      ]
    },
    {
      id: 2,
      name: 'María Figueroa',
      phone: '1154789632',
      email: 'maria.figueroa@gmail.com',
      address: 'Av. Corrientes 1234',
      personalMessage: '¡Vamos María! Estás progresando muy bien con la rutina de cardio.',
      files: [
        { id: 4, name: 'Rutina_Cardio.PDF' },
        { id: 5, name: 'Plan_Alimentacion.PDF' }
      ]
    },
    {
      id: 3,
      name: 'Juan Ledesma',
      phone: '1165478932',
      email: 'juan.ledesma@hotmail.com',
      address: 'Av. Rivadavia 5678',
      personalMessage: 'Juan, recordá estirar bien antes y después de la rutina de musculación.',
      files: [
        { id: 6, name: 'Rutina_Musculacion.PDF' }
      ]
    },
    {
      id: 4,
      name: 'Sofía Álvarez',
      phone: '1143215678',
      email: 'sofia.alvarez@gmail.com',
      address: 'Av. Santa Fe 910',
      personalMessage: 'Sofía, esta semana nos enfocamos en mejorar la técnica de los ejercicios.',
      files: [
        { id: 7, name: 'Tecnica_Ejercicios.PDF' },
        { id: 8, name: 'Plan_Proteico.PDF' }
      ]
    },
    {
      id: 5,
      name: 'Javier Altszyler',
      phone: '1123456789',
      email: 'javier@mail.com',
      address: 'Carlos Calvo 123',
      personalMessage: '¡Excelente progreso Javier! Seguí así con la constancia y los resultados seguirán llegando.',
      files: [
        { id: 9, name: 'Plan_Entrenamiento.PDF' },
        { id: 10, name: 'Evaluacion_Fisica.PDF' }
      ]
    }
  ];
};